import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { WelcomeLayout } from '@/components/glitch-ui/welcome-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { useToast } from '@/hooks/useToast';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { createOrganization, fetchOrganizations } from '@/lib/apis';
import { onboardingDataAtom } from '@/store';

export const Route = createFileRoute('/_authenticated/welcome/organization-name')({
  component: WelcomeOrganizationNameComponent,
});

function WelcomeOrganizationNameComponent() {
  const navigate = useNavigate();
  const router = useRouter();
  const onboardingData = useAtomValue(onboardingDataAtom);
  const [organizationName, setOrganizationName] = useState(() => onboardingData.organizationName || '');
  // Tell-on-click validation (#5857): Continue stays enabled; clicking it marks the empty field.
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const { toast } = useToast();
  const { organizations, switchOrganization } = useOrganizationInitialization();
  const inputRef = useRef<HTMLInputElement>(null);

  // Determine if user already has organizations (new org creation vs initial signup)
  const hasExistingOrganizations = organizations && organizations.length > 0;

  const handleClose = () => {
    // Use browser history to go back if available, otherwise fallback to business
    if (router.history.length > 1) {
      router.history.back();
    } else {
      navigate({ to: '/business' });
    }
  };

  // Focus input when component mounts
  useEffect(() => {
    // Auto-focus the input field
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleContinue = async () => {
    if (isSubmitting) return;
    if (!organizationName.trim()) {
      setNameError('Required');
      return;
    }

    setIsSubmitting(true);

    try {
      const createdOrg = await createOrganization({
        name: organizationName.trim(),
      });

      // Fetch updated organizations list and switch to the new one
      const orgs = await fetchOrganizations();
      const newOrg = orgs?.find(org => org.id === createdOrg.id);

      if (newOrg) {
        await switchOrganization(newOrg);
      }

      // Trigger completion animation
      setIsCompleting(true);

      // Navigate to business page after animation
      setTimeout(() => {
        navigate({ to: '/business' });
      }, 1500);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        description: 'Failed to create organization. Please try again.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <WelcomeLayout
      currentStep={1}
      title="Welcome. What is your organization's name?"
      isCompleting={isCompleting}
    >
      {/* Close button for users with existing organizations */}
      {hasExistingOrganizations && !isCompleting && (
        <button
          onClick={handleClose}
          className="absolute right-8 top-8 h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <X className="h-5 w-5 text-gray-700" />
          <span className="sr-only">Close</span>
        </button>
      )}

      {isCompleting ? (
        <motion.div
          className="flex items-center justify-center min-h-[400px]"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <motion.h2
              className="text-2xl font-bold text-gray-900 mb-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Organization Created!
            </motion.h2>
          </div>
        </motion.div>
      ) : (
        <motion.div
          className="space-y-8"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <div className="flex justify-center">
            <div className="w-[75%]">
              <Card className="rounded-lg border bg-white">
                <CardContent className="pt-6 space-y-6">
                  <div className="p-6">
                    <div className="max-w-md mx-auto space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="org-name-input" className="text-base font-medium text-gray-900">
                            Organization Name
                          </Label>
                          <FieldError message={nameError} />
                        </div>
                        <div className="relative">
                          <Input
                            ref={inputRef}
                            id="org-name-input"
                            placeholder="e.g. Glitch Ads"
                            value={organizationName}
                            onChange={(e) => {
                              setOrganizationName(e.target.value);
                              setNameError(undefined);
                            }}
                            disabled={isSubmitting}
                            error={!!nameError}
                            className={`h-[54px] w-full rounded-md border bg-transparent px-3 shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 text-base md:text-base ${nameError ? 'pr-8' : 'border-input focus-visible:ring-ring'}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleContinue();
                              }
                            }}
                          />
                          <InputAdornment error={!!nameError} />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <motion.div
                className="flex justify-end pt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Button
                  onClick={handleContinue}
                  disabled={!organizationName.trim() || isSubmitting}
                  size="lg"
                  className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>{isSubmitting ? 'CREATING...' : 'CREATE ORGANIZATION'}</span>
                  {!isSubmitting && <ArrowRight className="h-4 w-4" />}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </WelcomeLayout>
  );
}
